import {
  InfiniteData,
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import {DateTime} from 'luxon'

import {getApiRequestError, objectToForm} from '../utils'
import {GeneralPagedReponse, NetworkError, UploadFile} from '../models'

export type Header = {
  Authorization?: string
  'App-Version'?: string
  'Content-Type'?: string
  'X-Facility-Id'?: string
}

export type MutationRequestMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type MultiPartMutationParams<RequestType> = {
  body: RequestType
  file?: UploadFile | null
  clearFile?: boolean
}

export type ResponseNormaliserFn<ResponseType> = unknown extends ResponseType
  ? undefined
  : (data: ResponseType) => ResponseType

const tryFetch = async (url: string, init?: RequestInit) => {
  try {
    return await fetch(url, init)
  } catch (e) {
    const message =
      e instanceof Error
        ? e.message
        : 'API query failed due to a network error.'
    return Promise.reject(new NetworkError(message))
  }
}

export const isFormData = (object: unknown): object is FormData =>
  typeof object === 'object' &&
  object !== null &&
  object.constructor === FormData

export const getContentType = (object: unknown) =>
  isFormData(object) ? 'multipart/form-data' : 'application/json'

export const getMutationRequestBody = (requestBody: unknown) => {
  if (!requestBody) {
    return undefined
  }

  return isFormData(requestBody) ? requestBody : JSON.stringify(requestBody)
}

const parseResponseBody = async (response: Response) => {
  const contentLengthHeader = response.headers.get('content-length')
  const contentLength = contentLengthHeader && parseInt(contentLengthHeader, 10)

  if (contentLength === 0 || response.status === 204) {
    return null
  }

  const isResponseJSON = response.headers
    .get('content-type')
    ?.startsWith('application/json')

  return isResponseJSON ? response.json() : response.text()
}

const parseResponse = async <T>(
  response: Response,
  normaliser: (data: T) => T = (data) => data,
) => normaliser(await parseResponseBody(response))

export const getFetchFn =
  <T>(
    getHeader: () => Promise<Header>,
    endpoint: URL,
    normaliser: (data: T) => T = (data) => data,
  ) =>
  async () => {
    const headers = await getHeader()
    const response = await tryFetch(endpoint.href, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      return Promise.reject(
        await getApiRequestError(response, endpoint.href, headers),
      )
    }

    return parseResponse(response, normaliser)
  }

export type CushlaGetQueryOptions<T> = Omit<
  UseQueryOptions<T, Error, T, QueryKey>,
  'queryKey' | 'queryFn'
>

export const useGet = <T>(
  getHeader: () => Promise<Header>,
  key: QueryKey,
  endpoint: URL,
  normaliser: (data: T) => T,
  options: CushlaGetQueryOptions<T> = {},
) => {
  const client = useQueryClient()

  return useQuery<T, Error>(
    {
      queryKey: key,
      queryFn: getFetchFn(getHeader, endpoint, normaliser),
      retry: false,
      ...options,
    },
    client,
  )
}
export type CushlaInfiniteQueryOptions<T extends GeneralPagedReponse> = Omit<
  UseInfiniteQueryOptions<T, Error, InfiniteData<T>, QueryKey>,
  'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
>

export const useInfiniteGet = <T extends GeneralPagedReponse>(
  getHeader: () => Promise<Header>,
  key: QueryKey,
  endpoint: URL,
  normaliser: (data: T) => T,
  // callers can still pass options, but can't override the core bits we set
  options: CushlaInfiniteQueryOptions<T> = {},
) =>
  useInfiniteQuery<T, Error>({
    queryKey: [...key, 'infinite'] as const, // avoid conflicts with non-infinite queries
    initialPageParam: 0, // v5 requires this
    queryFn: async ({pageParam = 0}) => {
      // Avoid mutating a shared URL instance
      const url = new URL(endpoint.href)
      url.searchParams.set('page', String(pageParam))

      const headers = await getHeader()
      const response = await tryFetch(url.href, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        return Promise.reject(
          await getApiRequestError(response, url.href, headers),
        )
      }

      return parseResponse(response, normaliser)
    },
    getNextPageParam: (lastPage) => lastPage.page.next ?? undefined,
    retry: false,
    ...options,
  })

export type TimeSlotData<T> = T & {
  next?: string
  previous?: string
}

const getPreviousPageParamDate = (
  pageParamDate: DateTime,
  zone: string | undefined,
) => {
  const currentDay = DateTime.local({zone})
  if (currentDay.toISODate() === pageParamDate.toISODate()) {
    return undefined
  }

  const previousWeekDate = pageParamDate.minus({days: 7})
  return previousWeekDate > currentDay ? previousWeekDate : currentDay
}

export type UseInfiniteTimeSlotGetOptions<T> = Omit<
  UseInfiniteQueryOptions<
    TimeSlotData<T>,
    Error,
    InfiniteData<TimeSlotData<T>>,
    QueryKey
  >,
  | 'queryKey'
  | 'queryFn'
  | 'initialPageParam'
  | 'getNextPageParam'
  | 'getPreviousPageParam'
> & {
  initialDate: string
  timeZone: string | undefined
}

export const useInfiniteTimeSlotGet = <T>(
  getHeader: () => Promise<Header>,
  key: QueryKey,
  endpoint: URL,
  normaliser: (data: T) => T,
  initialDate: string,
  timeZone: string | undefined,
  options: UseInfiniteTimeSlotGetOptions<T>,
) =>
  useInfiniteQuery<
    TimeSlotData<T>,
    Error,
    InfiniteData<TimeSlotData<T>>,
    QueryKey
  >({
    queryKey: [...key, 'infinite'] as const,
    initialPageParam: initialDate,
    queryFn: async ({pageParam}) => {
      const from = (pageParam as string) ?? initialDate
      const fromDT = DateTime.fromISO(from)

      // Avoid mutating the shared URL instance
      const url = new URL(endpoint.href)
      url.searchParams.set('from', from)
      url.searchParams.set('to', fromDT.plus({days: 6}).toISODate()!)

      const headers = await getHeader()
      const response = await tryFetch(url.href, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        return Promise.reject(
          await getApiRequestError(response, url.href, headers),
        )
      }

      const base = await parseResponse(response, normaliser)

      return {
        ...base,
        next: fromDT.plus({days: 7}).toISODate()!,
        previous: getPreviousPageParamDate(fromDT, timeZone)?.toISODate(),
      } as TimeSlotData<T>
    },
    getNextPageParam: (data) => data.next,
    getPreviousPageParam: (data) => data.previous,
    gcTime: 0, // v5 rename of cacheTime
    retry: false,
    ...options,
  })

export const useSet = <RequestType, ResponseType, TContext = unknown>(
  getHeader: () => Promise<Header>,
  key: QueryKey,
  endpoint: URL,
  method: MutationRequestMethod,
  normaliser: ResponseNormaliserFn<ResponseType>,
  options: Omit<
    UseMutationOptions<ResponseType, Error, RequestType, TContext>,
    'mutationKey' | 'mutationFn'
  > = {},
  invalidationKeys: QueryKey[] = [],
) => {
  const client = useQueryClient()

  return useMutation<ResponseType, Error, RequestType, TContext>({
    mutationKey: key,
    mutationFn: async (data: RequestType) => {
      const authHeaders = await getHeader()
      const headers = {
        ...authHeaders,
        'Content-Type': getContentType(data),
      }

      const response = await tryFetch(endpoint.href, {
        method,
        headers,
        body: getMutationRequestBody(data),
      })

      if (!response.ok) {
        return Promise.reject(
          await getApiRequestError(response, endpoint.href, headers),
        )
      }

      return parseResponse(response, normaliser)
    },
    ...options,
    onSuccess: async (
      data,
      variables,
      onMutateResult, // <- new 3rd arg in v5
      context, // <- new 4th arg in v5
    ) => {
      await Promise.allSettled(
        invalidationKeys.map((k) => client.invalidateQueries({queryKey: k})),
      )
      await options.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const useMultiPartFormSet = <
  RequestType extends object,
  ResponseType = unknown,
  TContext = unknown,
>(
  getHeader: () => Promise<Header>,
  key: QueryKey,
  endpoint: URL,
  method: MutationRequestMethod,
  normaliser: ResponseNormaliserFn<ResponseType>,
  options: Omit<
    UseMutationOptions<
      ResponseType,
      Error,
      MultiPartMutationParams<RequestType>,
      TContext
    >,
    'mutationKey' | 'mutationFn'
  > = {},
  invalidationKeys: QueryKey[] = [],
) => {
  const client = useQueryClient()

  return useMutation<
    ResponseType,
    Error,
    MultiPartMutationParams<RequestType>,
    TContext
  >({
    mutationKey: key,
    mutationFn: async ({body, file, clearFile = false}) => {
      const authHeaders = await getHeader()

      // NOTE: When sending FormData, it's usually best NOT to set 'Content-Type'
      // so fetch can add the multipart boundary automatically.
      // Keep it only if your runtime requires it.
      const headers = {
        ...authHeaders,
        // 'Content-Type': 'multipart/form-data',
      } as Record<string, string>

      const formData = objectToForm({
        body: {string: JSON.stringify(body), type: 'application/json'},
        file,
        clearFile: {
          string: JSON.stringify(clearFile),
          type: 'application/json',
        },
      })

      const response = await tryFetch(endpoint.href, {
        method,
        headers,
        body: formData,
      })

      if (!response.ok) {
        return Promise.reject(
          await getApiRequestError(response, endpoint.href, headers),
        )
      }

      return parseResponse(response, normaliser)
    },
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.allSettled(
        invalidationKeys.map((k) => client.invalidateQueries({queryKey: k})),
      )
      await options.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const useVariableUrlSet = <
  UrlParams extends Record<string, string | boolean | null>,
  RequestType extends Record<string, string | number | boolean | null>,
  ResponseType,
  TContext = unknown,
>(
  getHeader: () => Promise<Header>,
  key: QueryKey,
  getEndpoint: (params: UrlParams) => URL,
  method: MutationRequestMethod,
  normaliser: ResponseNormaliserFn<ResponseType>,
  options: Omit<
    UseMutationOptions<
      ResponseType,
      Error,
      {urlParams: UrlParams; requestBody?: RequestType},
      TContext
    >,
    'mutationKey' | 'mutationFn'
  > = {},
  invalidationKeys: QueryKey[] = [],
) => {
  const client = useQueryClient()

  return useMutation<
    ResponseType,
    Error,
    {urlParams: UrlParams; requestBody?: RequestType},
    TContext
  >({
    mutationKey: key,
    mutationFn: async ({urlParams, requestBody = {} as RequestType}) => {
      const endpoint = getEndpoint(urlParams)
      const authHeaders = await getHeader()

      const headers = {
        ...authHeaders,
        'Content-Type': getContentType(requestBody),
      }

      const response = await tryFetch(endpoint.href, {
        method,
        headers,
        body: getMutationRequestBody(requestBody),
      })

      if (!response.ok) {
        return Promise.reject(
          await getApiRequestError(response, endpoint.href, headers),
        )
      }

      return parseResponse(response, normaliser)
    },
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.allSettled(
        invalidationKeys.map((k) => client.invalidateQueries({queryKey: k})),
      )
      await options.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
